import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { Dropdown } from "../../../../shadcn/components/custom/Dropdown";
import { Input } from "../../../../shadcn/components/ui/input";
import { Switch } from "../../../../shadcn/components/ui/switch";
import VerifiedHammer from "../../../../icons/VerifiedHammer";
import ChevronUpSvg from "../../../../icons/ChevronUpSvg";
import ChevronDownSvg from "../../../../icons/ChevronDownSvg";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../../shadcn/components/ui/popover";
import { Checkbox } from "../../../../shadcn/components/ui/checkbox";
import {
  Context,
  getSearchResults,
  formatIndianCurrency,
  getPrivatePropertySearchResults,
} from "services";
import Dot from "../../../../icons/Dot";
import PriceRangeSelector from "../../propertyList/PriceRangeSelector";
import { X } from "lucide-react";
import BrandedButton from "../../../../shadcn/components/custom/BrandedButton";
import { OptionType } from "./PrivatePropertyListPageWrapper";

interface PrivatePropertyListPageSidebarFilterMenuPropsInterface {
  filtersOpen: boolean;
  onClearFilters: () => void;
  onClose: () => void;
  onFiltersApply: () => void;
  setIsFilterOpen: (value: boolean) => void;
}

export const PrivatePropertyListPageSidebarFilterMenu: React.FC<
  PrivatePropertyListPageSidebarFilterMenuPropsInterface
> = ({
  onClearFilters = () => {},
  filtersOpen,
  onClose = () => {},
  onFiltersApply = () => {},
  setIsFilterOpen = () => {},
}: PrivatePropertyListPageSidebarFilterMenuPropsInterface) => {
  const { searchForm }: any = useContext(Context);

  const [assetTypeOptions, setAssetTypeOptions] = useState<any[]>([]);

  const filterData = (dataToFilter: any) => {
    return (dataToFilter || []).map((data: any) => ({
      label: data?.parent?.asset_type_name,
      value: data?.parent?.asset_type_slug,
    }));
  };

  const getFilterForSubAssetType = (searchForm: any) => {
    const filters = [];
    if (searchForm?.value?.asset_type?.value) {
      const currentFilterString = `dump.asset.asset_type_slug:=${searchForm?.value?.asset_type?.value}`;
      filters.push(currentFilterString);
    }
    return filters;
  };

  const fetchOptions = async (): Promise<any[]> => {
    const initialData: any = await getPrivatePropertySearchResults({
      searches: [
        {
          query_by: "",
          collection: "posted-properties",
          q: "*",
          facet_by: "dump.asset.asset_type_name,dump.asset.asset_type_slug,",
          facet_return_parent:
            "dump.asset.asset_type_name,dump.asset.asset_type_slug,",
          filter: "true",
          max_facet_values: "500",
          infix: "always",
        },
        {
          query_by: "",
          collection: "posted-properties",
          q: "*",
          facet_by:
            "dump.asset.sub_asset_type_name,dump.asset.sub_asset_type_slug",
          facet_return_parent:
            "dump.asset.sub_asset_type_name,dump.asset.sub_asset_type_slug",
          filter: "true",
          filter_by: getFilterForSubAssetType(searchForm).join(" && "),
          max_facet_values: "500",
          infix: "always",
        },
      ],
    });

    return (
      initialData?.results?.map((result: any) => ({
        group: "popular",
        data: filterData(result.facet_counts?.[0]?.counts),
      })) || []
    );
  };

  useEffect(() => {
    const initializeOptions = async () => {
      const data = await fetchOptions();
      setAssetTypeOptions(data[0]?.data || []);
    };
    initializeOptions();
  }, []);

  const propertyListingData = [
    { label: "Auction Properties ", value: "dated" },
    { label: "Private Properties ", value: "undated" },
  ];

  const handleListingTypeOption = (data: OptionType) => {
    if (data?.value === "dated") {
      window.location.href = "/auction-properties";
    } else {
      window.location.href =
        searchForm.value.city.value === ""
          ? "/private-properties"
          : `/private-properties?city=${searchForm?.value?.city?.value}`;
    }
  };

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const dataSource: () => Promise<any> = () => {
    return Promise.resolve(assetTypeOptions);
  };

  const handleSetSelectedOption = (item: any) => {
    searchForm.setField("asset_type", item);
    searchForm.setField("sub_asset_type", "");
  };

  const auctionVerifiedData = [
    { label: "Verified Property", value: "auction_verified" },
  ];

  const handleSwitchChange = (checked: boolean) => {
    const filterField = searchForm.getField("filter_by");
    if (checked) {
      searchForm.setField("ab_verified", {
        label: "Verified Property",
        value: "auction_verified",
      });
    } else {
      const isFilterVerified =
        filterField &&
        auctionVerifiedData?.some((item) => filterField.value === item.value);

      if (isFilterVerified) {
        searchForm.setField("filter_by", "");
      }
      searchForm.setField("ab_verified", "");
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    searchForm.setField("listing_id_query", value);
  };

  const handleClearFilter = () => {
    onClearFilters();
  };

  return (
    <section
      className={`fixed top-0 left-0 z-40 h-full w-[340px] bg-white border-r border-gray-200 p-4 overflow-y-auto transition-transform duration-300 ${
        filtersOpen ? "translate-x-0" : "-translate-x-full"
      }
  `}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-md md:text-xl lg:text-2xl font-semibold">
          Filters
        </h3>
        <button
          onClick={onClose}
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <X size={18} />
        </button>
      </div>

      <div className="mx-auto flex flex-col items-center gap-4 py-4 2xl:px-0">
        {/* LEFT FILTERS */}
        <div
          ref={scrollContainerRef}
          className="flex flex-col flex-1 items-center gap-3 w-full overflow-y-auto"
        >
          {/* SEARCH ID */}
          <Input
            type="text"
            placeholder="Search ID"
            value={searchForm.getField("listing_id_query") || ""}
            onChange={handleInputChange}
            className="h-9 w-full rounded-md border-gray-300 text-sm"
          />

          {/* ASSET TYPE */}
          <Dropdown
            dataSource={dataSource}
            defaultSelected={searchForm.getField("asset_type") || undefined}
            selectIdentifier
            onChange={handleSetSelectedOption}
            valueKey="value"
            placeholder="Asset Type"
            styles={{
              outerContainer:
                "h-9 rounded-md w-full border border-gray-300 px-4 text-sm",
            }}
          />

          {/* SUB ASSET */}
          <PropertySubTypeFilter />

          {/* PRICE */}
          <PriceRangeBasicDropDown />

          <Dropdown
            dataSource={() => {
              return Promise.resolve(propertyListingData);
            }}
            defaultSelected={
              searchForm.getField("filter_by") !== ""
                ? propertyListingData.find(
                    (item) =>
                      searchForm.getField("filter_by").value === item.value,
                  )
                : undefined
            }
            selectIdentifier={true}
            onChange={(data: any) => handleListingTypeOption(data)}
            valueKey="value"
            placeholder="Private Properties"
            styles={{
              dropdownItemsClass: "text-brand-borderGrey",
              hoverBgColor: "focus:bg-brand-lightPink",
              hoverTextColor: "focus:text-brand-borderGrey",
              outerContainer:
                "border-[1px] border-brand-borderGrey hover:border-brand-orange w-full rounded-lg text-sm leading-normal font-openSans text-brand-textBlack px-4",
            }}
          />

          {/* VERIFIED */}
          <div className="flex h-9 items-center gap-2 rounded-full bg-gray-100 px-3 w-full justify-center">
            <VerifiedHammer />

            <Switch
              checked={
                searchForm.getField("filter_by") !== ""
                  ? auctionVerifiedData.find(
                      (item) =>
                        searchForm.getField("filter_by").value === item.value,
                    )
                    ? true
                    : searchForm.getField("ab_verified") &&
                      searchForm.getField("ab_verified") !== ""
                    ? true
                    : false
                  : searchForm.getField("ab_verified") &&
                    searchForm.getField("ab_verified") !== ""
                  ? true
                  : false
              }
              onCheckedChange={handleSwitchChange}
            />
          </div>
        </div>
        <BrandedButton
          variant="primary"
          className="md:block hidden"
          fullWidth
          onClick={onFiltersApply}
        >
          Apply Filters
        </BrandedButton>
        <BrandedButton
          variant="primary"
          className="md:hidden block"
          fullWidth
          onClick={() => {
            onFiltersApply();
            setIsFilterOpen(false);
          }}
        >
          Apply Filters
        </BrandedButton>

        <BrandedButton
          variant="outline"
          className="md:block hidden"
          fullWidth
          onClick={handleClearFilter}
        >
          Clear Filters
        </BrandedButton>
        <BrandedButton
          variant="outline"
          fullWidth
          className="md:hidden block"
          onClick={() => {
            handleClearFilter();
            setIsFilterOpen(false);
          }}
        >
          Clear Filters
        </BrandedButton>
      </div>
    </section>
  );
};

const PriceRangeBasicDropDown = () => {
  const { searchForm }: any = useContext(Context);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState<
    [number | null, number | null]
  >([0, null]);

  // Update the reserve price field when the selected range changes
  useEffect(() => {
    const reservePrice = searchForm.value.reserve_price;
    if (reservePrice) {
      const [minPrice, maxPrice] = reservePrice.split("-").map(Number);
      setSelectedRange([minPrice * 100000, maxPrice * 100000]);
    } else {
      setSelectedRange([0, null]);
    }
  }, [searchForm.value.reserve_price]);

  useEffect(() => {
    const updateReservePrice = () => {
      if ((selectedRange[0] || selectedRange[0] === 0) && selectedRange[1]) {
        const formattedRange = `${Math.floor(
          selectedRange[0] / 100000,
        )}-${Math.floor(selectedRange[1] / 100000)}`;
        if (isOpen && selectedRange[0] < selectedRange[1]) {
          searchForm.setField("reserve_price", formattedRange);
        }
      }
    };

    const handler = setTimeout(updateReservePrice, 300);
    return () => clearTimeout(handler);
  }, [selectedRange, isOpen]);

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger asChild>
        <main
          className={`cursor-pointer border-[1px] ${
            isOpen ? "border-brand-orange" : "border-brand-borderGrey"
          } hover:border-brand-orange min-w-[130px] w-full rounded-lg pl-4 pr-2 py-[9px]`}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <div className="flex items-center justify-between">
            {searchForm.getField("reserve_price") &&
            searchForm.getField("reserve_price") !== "" ? (
              <p className="text-[13px] font-openSans text-brand-textBlack flex items-center whitespace-nowrap">
                <span className="mr-3">
                  <Dot />
                </span>
                ₹{formatIndianCurrency(selectedRange[0])} - ₹
                {formatIndianCurrency(selectedRange[1])}
              </p>
            ) : (
              <p className="text-[14px] font-openSans text-brand-textBlack flex items-center whitespace-nowrap">
                Price Range
              </p>
            )}
            <div className="pr-2 pl-2">
              {isOpen ? (
                <ChevronUpSvg width={14} height={14} fill="#7D7D7D" />
              ) : (
                <ChevronDownSvg width={14} height={14} fill="#7D7D7D" />
              )}
            </div>
          </div>
        </main>
      </PopoverTrigger>
      <PopoverContent className="min-w-[439px]">
        <PriceRangeSelector
          range={[0, 2600000000]}
          defaultSelectedRange={selectedRange}
          minStepsBetweenThumbs={500}
          step={500}
          setSelectedRange={setSelectedRange}
        />
      </PopoverContent>
    </Popover>
  );
};

const PropertySubTypeFilter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { searchForm }: any = useContext(Context);
  const initialCheckedItems = searchForm.value.asset_type
    ? Array.isArray(searchForm.value.sub_asset_type)
      ? searchForm.value.sub_asset_type
      : typeof searchForm.value.sub_asset_type === "object" &&
        searchForm.value.sub_asset_type !== ""
      ? [searchForm.value.sub_asset_type]
      : []
    : [];

  const [assetSubTypeOptions, setAssetSubTypeOptions] = useState<any[]>([]);

  const filterData = useCallback((dataToFilter: any) => {
    return (dataToFilter || []).map((data: any) => ({
      label: data.parent.sub_asset_type_name,
      value: data.parent.sub_asset_type_slug,
    }));
  }, []);

  const getFilterForSubAssetType = (searchForm: any) => {
    const filters = [];
    if (searchForm.value.asset_type.value) {
      const currentFilterString = `dump.asset.asset_type_slug:=${searchForm.value.asset_type.value}`;
      filters.push(currentFilterString);
    }
    return filters;
  };

  const fetchOptions = async (): Promise<any[]> => {
    const initialData: any = await getSearchResults({
      searches: [
        {
          query_by: "",
          collection: "properties",
          q: "*",
          facet_by:
            "dump.asset.sub_asset_type_name,dump.asset.sub_asset_type_slug",
          facet_return_parent:
            "dump.asset.sub_asset_type_name,dump.asset.sub_asset_type_slug",
          filter: "true",
          filter_by: getFilterForSubAssetType(searchForm).join(" && "),
          max_facet_values: "500",
          infix: "always",
        },
      ],
    });

    return (
      initialData?.results?.map((result: any) => ({
        group: "popular",
        data: filterData(result.facet_counts?.[0]?.counts),
      })) || []
    );
  };

  useEffect(() => {
    if (searchForm.value.asset_type.value) {
      const initializeOptions = async () => {
        const data = await fetchOptions();
        setAssetSubTypeOptions(data[0]?.data || []);
        // setCheckedItems(initialCheckedItems);
      };
      initializeOptions();
    } else {
      setAssetSubTypeOptions([]);
    }
  }, [searchForm.value.asset_type]);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleCheckboxChange = (
    item: { label: string; value: string },
    isChecked: boolean,
  ) => {
    const updatedCheckedItems = isChecked
      ? [...initialCheckedItems, item]
      : initialCheckedItems.filter(
          (checkedItem: { value: string }) => checkedItem.value !== item.value,
        );
    searchForm.setField("sub_asset_type", updatedCheckedItems);
  };

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger asChild>
        <main
          className={`cursor-pointer border-[1px] ${
            isOpen ? "border-brand-orange" : "border-brand-borderGrey"
          } hover:border-brand-orange min-w-[130px] w-full rounded-lg pl-4 pr-2 py-[9px]`}
          onClick={toggleDropdown}
        >
          <div className="flex items-center justify-between">
            <div className="text-sm font-openSans text-brand-textBlack capitalize overflow-hidden break-all max-h-5 flex items-center">
              {initialCheckedItems.length > 0 && (
                <span className="mr-3">
                  <Dot />
                </span>
              )}

              <span className="overflow-hidden text-ellipsis whitespace-nowrap capitalize">
                {initialCheckedItems.length > 0 ? (
                  <>
                    {initialCheckedItems[0].label}
                    {initialCheckedItems.length > 1 && (
                      <>,{initialCheckedItems[1].label.slice(0, 3)}...</>
                    )}
                  </>
                ) : (
                  "Sub Asset Type "
                )}
              </span>
            </div>
            <div className="pr-2 pl-2">
              {isOpen ? (
                <ChevronUpSvg width={14} height={14} fill={"#7D7D7D"} />
              ) : (
                <ChevronDownSvg width={14} height={14} fill={"#7D7D7D"} />
              )}
            </div>
          </div>
        </main>
      </PopoverTrigger>
      <PopoverContent className="min-w-[130px] xl:min-w-[160px] w-full">
        <div className="max-h-[251px] overflow-scroll w-full">
          {assetSubTypeOptions.map((item, index) => (
            <div key={index} className="flex items-center">
              <Checkbox
                id={`asset-${index}`}
                checkIconClassName="text-brand-bgBlack"
                className="data-[state=checked]:bg-[#EEEEEE] border-brand-bgBlack"
                checked={initialCheckedItems?.some(
                  (checkedItem: any) => checkedItem.value === item.value,
                )}
                onCheckedChange={(isChecked: boolean) =>
                  handleCheckboxChange(item, isChecked)
                }
              />
              <label
                htmlFor={`asset-${index}`}
                className="text-sm pl-2 py-1 font-openSans capitalize tracking-wider text-brand-textLightGrey"
              >
                {item.label}
              </label>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
